import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import di from "../../../../dependency_injection";
import SearchResultEntity from "../../../../domain/entities/search_result.entity";
import HumanAgentEntity, { HumanAgentStatus } from "../../../../domain/entities/human_agent.entity";
import DeleteHumanAgentUseCase, { DeleteHumanAgentUseCaseName } from "../../../../domain/use_cases/human_agent/delete_human_agent.use_case";
import SearchHumanAgentsUseCase, { SearchHumanAgentsUseCaseName } from "../../../../domain/use_cases/human_agent/search_human_agents.use_case";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import routes from "../../../routes/Routes";
import CreateHumanAgentComponent from "../create/create_human_agent.component";
import DownladAllHumanAgentsUseCase, { DownladAllHumanAgentsUseCaseName } from "../../../../domain/use_cases/human_agent/download_all_human_agents.use_case";

const useHumanAgentListViewModel = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchQuery = searchParams.get("search");
    const pageQuery = parseInt(searchParams.get("page") ?? "1");
    const statusQuery = searchParams.get("status") as HumanAgentStatus;

    const { i18n } = useContext(LocalizationContext);
    const { addToast, openModalDelete, openModalCustom, closeModalCustom, closeModalDelete } = useContext(ModalsContext);
    const { register, handleSubmit, getValues, setValue, watch, formState: { errors }, reset } = useForm();
    const [result, setResult] = useState<SearchResultEntity<HumanAgentEntity> | undefined>(undefined);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const navigate = useNavigate();
    const itemsPerPage = 20;
    let _timerTap: any;

    const _searchHumanAgents = async () => {
        const data = getValues();
        clearTimeout(_timerTap);
        setResult(undefined);
        const response = await di.get<SearchHumanAgentsUseCase>(SearchHumanAgentsUseCaseName).call({
            query: data[KeyWordLocalization.HumanAgentListPageQuery],
            status: data[KeyWordLocalization.HumanAgentEntityStatus],
            page: data[KeyWordLocalization.HumanAgentListCurrentPage],
            itemsPerPage
        });
        if (isLeft(response)) {
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        setResult(response.right);
    };

    const _handleSearch = (data: any) => {
        const search = data[KeyWordLocalization.HumanAgentListPageQuery];
        const page = data[KeyWordLocalization.HumanAgentListCurrentPage];
        const status = data[KeyWordLocalization.HumanAgentEntityStatus];
        let query = "?";
        if (search && search.length > 0) query += `search=${search}&`;
        if (page) query += `page=${page}&`;
        if (status) query += `status=${status}&`;
        if (query.endsWith("&")) query = query.slice(0, -1);
        navigate(routes.human_agent_list.relativePath + query);
    };

    const _handleChangeTextSearch = () => {
        clearTimeout(_timerTap);
        const data = getValues();
        _timerTap = setTimeout(() => _handleSearch(data), 500);
    };

    const _handleChange = () => {
        const data = getValues();
        _handleSearch(data);
    };

    const _handleLoad = () => {
        setValue(KeyWordLocalization.HumanAgentListPageQuery, searchQuery);
        setValue(KeyWordLocalization.HumanAgentListCurrentPage, pageQuery);
        setValue(KeyWordLocalization.HumanAgentEntityStatus, statusQuery);
        _searchHumanAgents();
    };

    const _handleOnChangePage = (page: number) => {
        setValue(KeyWordLocalization.HumanAgentListCurrentPage, page);
        const data = getValues();
        _handleSearch(data);
    };

    const _handleOnClickAgent = (agent: HumanAgentEntity) =>
        navigate(routes.human_agent.relativePathWithParams({ id: agent.id }));

    const _handleOnClickDeleteAgent = async (agent: HumanAgentEntity) => {
        const deleteAgent = async () => {
            const response = await di.get<DeleteHumanAgentUseCase>(DeleteHumanAgentUseCaseName).call(agent.id);
            if (isLeft(response)) return addToast(response.left.code ?? KeyWordLocalization.UnknownError, 'error', undefined);
            closeModalDelete();
            addToast(i18n(KeyWordLocalization.HumanAgentListPageDeleteSuccess), 'success', undefined);
            _searchHumanAgents();
        };
        openModalDelete(
            i18n(KeyWordLocalization.HumanAgentListDeleteModalTitle),
            i18n(KeyWordLocalization.HumanAgentListDeleteModalSubtitle),
            deleteAgent
        );
    };

    const _handleOnClickUpdateAgent = (agent: HumanAgentEntity) => {
        const _onSuccess = () => {
            closeModalCustom();
            _searchHumanAgents();
        };
        openModalCustom(
            "lg",
            i18n(KeyWordLocalization.HumanAgentListPageUpdateAgent),
            <CreateHumanAgentComponent humanAgent={agent} onCreated={_onSuccess} />,
            { hideOnTap: false }
        );
    };

    const _handleOnCreateSingleAgent = () => {
        const _onSuccess = () => {
            closeModalCustom();
            _searchHumanAgents();
        };
        openModalCustom(
            "lg",
            i18n(KeyWordLocalization.HumanAgentListPageAddAgent),
            <CreateHumanAgentComponent onCreated={_onSuccess} />
        );
    };

    const _handleDownloadAgents = async () => {
        setLoadingDownload(true);
        const response = await di.get<DownladAllHumanAgentsUseCase>(DownladAllHumanAgentsUseCaseName).call();
        setLoadingDownload(false);
        if (isLeft(response)) {
            return addToast(response.left.code ?? KeyWordLocalization.UnknownError, 'error', undefined);
        }
        addToast(i18n(KeyWordLocalization.HumanAgentListPageDownloadSuccess), 'success', undefined);
    };

    const _handleOnBulkImport = () => navigate(routes.bulk_human_agent.relativePath);

    useEffect(() => {
        _handleLoad();
    }, [pageQuery, searchQuery, statusQuery]);

    return {
        formInfo: { register, handleSubmit, getValues, setValue, watch, errors, reset },
        result,
        loadingDownload,
        reset,
        i18n,
        _handleSearch,
        _handleChangeTextSearch,
        _handleChange,
        _handleOnChangePage,
        _handleOnClickAgent,
        _handleOnClickDeleteAgent,
        _handleOnClickUpdateAgent,
        _handleOnCreateSingleAgent,
        _handleDownloadAgents,
        _handleOnBulkImport,
    };
};

export default useHumanAgentListViewModel;